import React, { useMemo, useState } from "react";
import moment from "moment-timezone";
import {
  Stack,
  Box,
  Skeleton,
  Typography,
  Tooltip,
  Chip,
  Autocomplete,
  TextField,
  useMediaQuery
} from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";

import {
  HostType,
  InterviewerType,
  UiStateType,
  useSchedulerStore
} from "../../state/scheduler.store";
import { MeetingTime } from "./MeetingTime";
import { TZ } from "../../utils/timezones";
import { useScreenSize } from "../../hooks/useScreenSize";
import { useTranslation } from "react-i18next";
import { formatDurationForMeetingDescription } from "../../utils/duration";
import { useI18nLanguage } from "../../hooks/i18nLanguage";

interface IMeetingInfoProps {
  clientLogo?: string;
  uiStep: UiStateType["currentStep"];
  meetingDuration: number | null;
  participants: InterviewerType[] | null;
  selectedTimezone: string | null;
  host: HostType | null;
  isRoosterLink: boolean;
  timeSlots: {
    currentlySelectedSlot: string | null;
    previouslySelectedTime: string | null;
  };
  handleTimezoneChange: (tz: string) => void;
}

export const MeetingAutobookInfo: React.FC<IMeetingInfoProps> = ({
  uiStep,
  clientLogo,
  meetingDuration,
  participants,
  host,
  isRoosterLink,
  selectedTimezone,
  timeSlots,
  handleTimezoneChange
}) => {
  const { selectedLanguageForMoment } = useI18nLanguage();
  const { meetingLink, loadingAvailability, ui, sessionData } =
    useSchedulerStore();
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const { isLargeScreen, isSmallScreen } = useScreenSize();
  const showTimezoneLabel =
    useMediaQuery("(min-height: 720px)") && !isSmallScreen;
  const { t, i18n } = useTranslation(); // Added translation hook
  const isRescheduleFlow = timeSlots.previouslySelectedTime !== null;

  // A null duration means the link never finished initialising (for example the
  // sender-grant check bailed out before setMeetingLink ran). Interpolating it
  // rendered the literal string "null minutes" to the candidate, so treat an
  // unknown duration the same as still-loading rather than printing a value.
  const knownDuration =
    typeof meetingDuration === "number" && Number.isFinite(meetingDuration)
      ? meetingDuration
      : null;

  // The icon and the text are one visual unit: gating only the text left the
  // real clock icon sitting beside a shimmering skeleton, which reads as a
  // half-loaded row.
  const isDurationReady = uiStep !== "loading" && knownDuration !== null;

  const { title, description } = useMemo(() => {
    if (meetingLink?.type === "rooster") {
      const description = t("ROOSTER.PUBLIC-APP.CHOOSE_INTERVIEW_TIME", {
        defaultValue:
          "Please choose a date and time for your interview lasting {{duration}}.",
        duration: formatDurationForMeetingDescription(
          meetingLink?.availability?.duration ?? 0,
          t,
          selectedLanguageForMoment
        )
      });

      if (meetingLink?.hasDynamicInterviewersFilter && !isRescheduleFlow) {
        return { title: "", description };
      }

      const interviewers = meetingLink?.interviewers;
      let title = `${t("ROOSTER.PUBLIC-APP.YOUR_INTERVIEW_WITH", {
        defaultValue: "Your interview with "
      })} `;
      if (interviewers?.length === 1) {
        title += interviewers[0].name;
      } else if (interviewers?.length === 2) {
        title += t("ROOSTER.PUBLIC-APP.TWO_INTERVIEWERS", {
          defaultValue: "{{interviewer1}} and {{interviewer2}}",
          interviewer1: interviewers[0].name,
          interviewer2: interviewers[1].name
        });
      } else if (interviewers && interviewers?.length > 2) {
        title += t("ROOSTER.PUBLIC-APP.MULTIPLE_INTERVIEWERS", {
          defaultValue: "{{interviewer}} and {{count}} more people",
          interviewer: interviewers[0].name,
          count: interviewers.length - 1
        });
      }

      return {
        title,
        description
      };
    }
    return {
      title: meetingLink?.roosterLinksMeta?.title,
      description: meetingLink?.roosterLinksMeta?.description
    };
  }, [
    meetingLink?.roosterLinksMeta,
    meetingLink?.interviewers,
    meetingLink?.availability?.duration,
    meetingLink?.hasDynamicInterviewersFilter,
    i18n.language,
    t,
    isRescheduleFlow,
    selectedLanguageForMoment
  ]);

  return (
    <Stack
      height="100%"
      width="100%"
      justifyContent={"flex-start"}
      sx={{
        "&::-webkit-scrollbar": {
          width: "4px",
          borderRadius: "10px"
        },
        "&:hover": {
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "10px"
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#555"
          }
        },
        mt: !isLargeScreen ? 5.5 : 0,
        maxHeight:
          isLargeScreen && ui?.currentView === "month" ? "520px" : "auto" // Added maxHeight for large screens
      }}
      data-testid="meeting-autobook-info-container"
    >
      {/* Client Logo */}
      <Stack alignItems="flex-start" data-testid="client-logo-container">
        <div style={{ height: 48 }}>
          {!isImageLoaded && (
            <Skeleton
              variant="rounded"
              height={48}
              width={170}
              data-testid="client-logo-skeleton"
            />
          )}
          {clientLogo && (
            <img
              alt={t("ROOSTER.PUBLIC-APP.CLIENT_LOGO", {
                defaultValue: "client logo"
              })}
              onError={() => {
                setIsImageLoaded(false);
              }}
              onLoad={() => {
                setIsImageLoaded(true);
              }}
              src={clientLogo}
              style={{
                height: 48,
                maxWidth: 180,
                objectFit: "contain",
                display: isImageLoaded ? "block" : "none"
              }}
              data-testid="client-logo-image"
            />
          )}
        </div>
      </Stack>

      {/* Scrollable Content */}
      <Stack
        overflow="auto"
        sx={{
          "&::-webkit-scrollbar": {
            width: "4px",
            borderRadius: "10px"
          },
          "&:hover": {
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: "10px"
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555"
            }
          }
        }}
      >
        <Stack sx={{ marginTop: 2 }} data-testid="meeting-info-content">
          {uiStep === "loading" && (
            <Box
              role="status"
              aria-live="polite"
              sx={{
                position: "absolute",
                width: "1px",
                height: "1px",
                padding: 0,
                margin: "-1px",
                overflow: "hidden",
                clip: "rect(0, 0, 0, 0)",
                whiteSpace: "nowrap",
                borderWidth: 0
              }}
            >
              {t("ROOSTER.PUBLIC-APP.LOADING_MEETING_TITLE", {
                defaultValue: "Loading meeting information"
              })}
            </Box>
          )}
          <Typography
            component="h1"
            aria-label={
              uiStep === "loading"
                ? t("ROOSTER.PUBLIC-APP.LOADING_MEETING_TITLE", {
                    defaultValue: "Loading meeting information"
                  })
                : undefined
            }
            aria-busy={uiStep === "loading"}
            sx={{
              fontSize: "18px",
              fontWeight: "bold",
              wordBreak: "break-word"
            }}
            data-testid="meeting-title-heading"
          >
            {uiStep !== "loading" ? (
              title
            ) : (
              <Skeleton
                variant="text"
                height={22}
                width={"160px"}
                data-testid="meeting-title-skeleton"
              />
            )}
          </Typography>
          <Typography
            sx={{
              marginTop: 1,
              marginBottom: 1
            }}
            data-testid="meeting-description-container"
          >
            {uiStep !== "loading" ? (
              <Tooltip
                title={
                  <p style={{ fontSize: "16px", margin: "0", padding: "0" }}>
                    {description || ""}
                  </p>
                }
                disableHoverListener={!description}
                placement="bottom"
                enterDelay={300}
                slotProps={{
                  popper: {
                    disablePortal: true
                  }
                }}
              >
                <Typography
                  component="p"
                  fontSize="16px"
                  fontWeight={400}
                  variant="subtitle1"
                  tabIndex={0}
                  sx={{
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                  data-testid="meeting-description-text"
                >
                  {description}
                </Typography>
              </Tooltip>
            ) : (
              <Stack data-testid="meeting-description-skeleton">
                <Skeleton variant="text" height={18} width={"140px"} />
                <Skeleton variant="text" height={18} width={"280px"} />
                <Skeleton variant="text" height={22} width={"280px"} />
                <Skeleton variant="text" height={22} width={"280px"} />
                <Skeleton variant="text" height={22} width={"100px"} />
              </Stack>
            )}
          </Typography>
        </Stack>

        {/* Meeting Time Information */}
        {uiStep !== "loading" ? (
          <MeetingTime
            selectedTime={timeSlots.currentlySelectedSlot}
            selectedTimezone={selectedTimezone}
            previouslySelectedTime={timeSlots.previouslySelectedTime}
          />
        ) : (
          <Stack data-testid="meeting-time-skeleton">
            <Skeleton variant="text" height={25} width={"85%"} />
            <Skeleton variant="text" height={25} width={"45%"} />
          </Stack>
        )}

        {/* Meeting Duration, Meeting Participants and Timezone selector*/}
        <Stack data-testid="meeting-details-section">
          <Stack
            fontSize="16px"
            direction="row"
            spacing={2}
            alignItems="center"
            data-testid="meeting-duration-row"
          >
            <Stack>
              {isDurationReady ? (
                <AccessTimeOutlinedIcon
                  fontWeight="bold"
                  data-testid="duration-icon"
                />
              ) : (
                <Skeleton
                  variant="text"
                  height={45}
                  width={"30px"}
                  data-testid="duration-icon-skeleton"
                />
              )}
            </Stack>
            <Stack>
              <Typography
                fontSize="inherit"
                fontWeight={450}
                data-testid="meeting-duration-text"
              >
                {isDurationReady ? (
                  knownDuration > 60 ? (
                    moment
                      .utc()
                      .startOf("day")
                      .add({
                        minutes: knownDuration
                      })
                      .format("H[h]:mm[m]")
                  ) : (
                    `${knownDuration} ${t("ROOSTER.PUBLIC-APP.MINUTES", {
                      defaultValue: "minutes"
                    })}`
                  )
                ) : (
                  <Skeleton
                    variant="text"
                    height={45}
                    width={"170px"}
                    data-testid="duration-skeleton"
                  />
                )}
              </Typography>
            </Stack>
          </Stack>

          {(meetingLink?.hasDynamicInterviewersFilter === false ||
            sessionData?.isRescheduleFlow === true) && (
            <Stack
              pt={2}
              fontSize="16px"
              direction="row"
              spacing={2}
              alignItems="flex-start"
              data-testid="meeting-participants-row"
            >
              <Stack>
                {uiStep !== "loading" ? (
                  <AccountCircleOutlinedIcon
                    fontWeight={450}
                    data-testid="participants-icon"
                  />
                ) : (
                  <Skeleton
                    variant="text"
                    height={45}
                    width={"30px"}
                    data-testid="participants-icon-skeleton"
                  />
                )}
              </Stack>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    !isLargeScreen ||
                    (Array.isArray(participants) && participants?.length > 4)
                      ? "1fr auto"
                      : "1fr",
                  gap: "6px 18px",
                  overflow: "auto",
                  paddingRight: "8px !important",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                    background: "transparent",
                    borderRadius: "10px"
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "transparent",
                    backgroundClip: "padding-box",
                    borderRadius: "10px"
                  },
                  "&:hover": {
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "#888",
                      backgroundClip: "padding-box"
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555"
                    }
                  }
                }}
                data-testid="participants-list"
              >
                {uiStep !== "loading" ? (
                  participants?.map((participant, index) => (
                    <Box
                      key={participant.email}
                      sx={{
                        margin: "0 !important"
                      }}
                      data-testid={`participant-item-${index}`}
                    >
                      <Tooltip
                        title={participant.name}
                        slotProps={{
                          popper: {
                            disablePortal: true
                          }
                        }}
                      >
                        <Typography
                          fontSize={"16px"}
                          fontWeight={450}
                          textOverflow="ellipsis"
                          maxWidth="100%"
                          variant="caption"
                          tabIndex={0}
                          sx={{ textTransform: "capitalize" }}
                          data-testid={`participant-name-${index}`}
                        >
                          {participant.name}{" "}
                          {participant?.name?.toLowerCase() ===
                            host?.name?.toLowerCase() && (
                            <Chip
                              label={
                                isRoosterLink
                                  ? t("ROOSTER.PUBLIC-APP.HOST", {
                                      defaultValue: "Host"
                                    })
                                  : t("ROOSTER.PUBLIC-APP.SCHEDULER", {
                                      defaultValue: "Scheduler"
                                    })
                              }
                              variant="outlined"
                              size="small"
                              data-testid={`participant-host-chip-${index}`}
                            />
                          )}
                        </Typography>
                      </Tooltip>
                    </Box>
                  ))
                ) : (
                  <Skeleton
                    variant="text"
                    height={45}
                    width={170}
                    data-testid="participants-skeleton"
                  />
                )}
              </Box>
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={2}
            sx={{ marginTop: "16px" }}
            data-testid="timezone-row"
          >
            {uiStep !== "loading" ? (
              <PublicOutlinedIcon data-testid="timezone-icon" />
            ) : (
              <Skeleton
                variant="text"
                height={45}
                width={30}
                data-testid="timezone-icon-skeleton"
              />
            )}
            <Stack
              direction={isSmallScreen ? "row" : "column"}
              sx={{ gap: "12px" }}
              data-testid="timezone-selector-container"
            >
              {showTimezoneLabel && (
                <Typography
                  fontSize="inherit"
                  fontWeight={450}
                  data-testid="timezone-label"
                >
                  {uiStep !== "loading" ? (
                    t("ROOSTER.PUBLIC-APP.TIMEZONE", {
                      defaultValue: "Timezone"
                    })
                  ) : (
                    <Skeleton
                      variant="text"
                      height={20}
                      width={100}
                      data-testid="timezone-label-skeleton"
                    />
                  )}
                </Typography>
              )}
              {uiStep !== "loading" ? (
                <Autocomplete
                  options={TZ}
                  value={selectedTimezone ?? moment.tz.guess()}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      handleTimezoneChange(newValue);
                    }
                  }}
                  disableClearable
                  getOptionLabel={(option) => {
                    const offset = moment.tz(option).format("Z");
                    return `(${offset}) ${option}`;
                  }}
                  slotProps={{
                    popper: {
                      disablePortal: true,
                      sx: {
                        width: "300px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "4px",
                        overflow: "ellipsis",
                        "& .MuiAutocomplete-listbox": {
                          "&::-webkit-scrollbar": {
                            width: "4px",
                            background: "transparent",
                            borderRadius: "10px"
                          },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "transparent",
                            backgroundClip: "padding-box",
                            borderRadius: "10px"
                          },
                          "&:hover": {
                            "&::-webkit-scrollbar-thumb": {
                              backgroundColor: "#888",
                              backgroundClip: "padding-box"
                            },
                            "&::-webkit-scrollbar-thumb:hover": {
                              backgroundColor: "#555"
                            }
                          }
                        }
                      }
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      inputProps={{
                        ...params.inputProps,
                        "aria-label": t("ROOSTER.PUBLIC-APP.SELECT_TIMEZONE", {
                          defaultValue: "Select timezone"
                        })
                      }}
                      sx={{
                        ".MuiAutocomplete-inputRoot": {
                          padding: "0px 6px",
                          minWidth: "230px",
                          width: "fit-content",
                          maxWidth: "100%"
                        }
                      }}
                      data-testid="timezone-input"
                    />
                  )}
                  disabled={uiStep !== "initial" || loadingAvailability}
                  sx={{
                    width: "auto",
                    minWidth: "200px",
                    maxWidth: "100%"
                  }}
                  data-testid="timezone-autocomplete"
                />
              ) : (
                <Skeleton
                  variant="text"
                  height={50}
                  width="100%"
                  data-testid="timezone-selector-skeleton"
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};
